import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { ChatMessage, ChatSession, UserChatMessage } from '@/types'
import {
  sendMessage as apiSendMessage,
  getSession as apiGetSession,
  getSessions as apiGetSessions,
} from '@/services/api'
import useAuthStore from './useAuthStore'

interface ChatState {
  sessionId: string | null
  messages: ChatMessage[]
  sessions: ChatSession[]
  isLoading: boolean
  error: string | null
  organizationId: string | null
  recoveryIndicator: 'hydrated' | 'loaded' | null
  startNewSession: () => void
  loadSession: (sessionId: string) => Promise<void>
  sendMessage: (content: string) => Promise<void>
  fetchSessions: () => Promise<void>
  setOrganizationId: (organizationId: string) => void
  setRecoveryIndicator: (status: 'hydrated' | 'loaded' | null) => void
}

const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      sessionId: null,
      messages: [],
      sessions: [],
      isLoading: false,
      error: null,
      organizationId: null,
      recoveryIndicator: null,

      startNewSession: () => {
        set({
          sessionId: crypto.randomUUID(),
          messages: [],
          error: null,
          organizationId: null,
          recoveryIndicator: null,
        })
      },

      loadSession: async (sessionId) => {
        set({ isLoading: true, error: null })
        try {
          const session = await apiGetSession(sessionId)
          if (session) {
            const firstUserMessage = session.messages?.find(
              (m): m is UserChatMessage => m.role === 'user',
            )
            const organizationId =
              firstUserMessage?.metadata?.organization_id || null

            set({
              sessionId: session.id,
              messages: session.messages || [],
              organizationId,
              isLoading: false,
              recoveryIndicator: 'loaded',
            })
          } else {
            throw new Error('Session not found.')
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to load session.'
          set({ error: errorMessage, isLoading: false })
        }
      },

      sendMessage: async (content: string) => {
        const user = useAuthStore.getState().user
        if (!user) {
          set({ error: 'User not authenticated.', isLoading: false })
          return
        }

        const { sessionId, organizationId: storedOrgId } = get()
        const orgIdToUse = storedOrgId

        if (!orgIdToUse) {
          set({
            error: 'Organization ID is missing for the chat session.',
            isLoading: false,
          })
          return
        }

        const isNewSession = !sessionId
        const sessionIdForMessage = sessionId ?? crypto.randomUUID()

        if (isNewSession) {
          set({ organizationId: orgIdToUse })
        }

        const userMessage: UserChatMessage = {
          role: 'user',
          content,
          timestamp: new Date().toISOString(),
          metadata: {
            session_id: sessionIdForMessage,
            user_id: user.id,
            organization_id: orgIdToUse,
          },
        }

        set((state) => ({
          messages: [...state.messages, userMessage],
          isLoading: true,
          error: null,
        }))

        try {
          const assistantMessage = await apiSendMessage(userMessage)
          const newSessionId = assistantMessage.metadata?.session_id
          set((state) => ({
            messages: [...state.messages, assistantMessage],
            sessionId: newSessionId || state.sessionId,
            isLoading: false,
          }))
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to get a response from the assistant.'
          set({
            error: errorMessage,
            isLoading: false,
          })
        }
      },

      fetchSessions: async () => {
        set({ isLoading: true, error: null })
        try {
          const sessions = await apiGetSessions()
          set({ sessions, isLoading: false })
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to fetch sessions.'
          set({ error: errorMessage, isLoading: false, sessions: [] })
        }
      },

      setOrganizationId: (organizationId: string) => {
        set({ organizationId })
      },

      setRecoveryIndicator: (status) => {
        set({ recoveryIndicator: status })
      },
    }),
    {
      name: 'chat-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sessionId: state.sessionId,
        messages: state.messages,
        organizationId: state.organizationId,
      }),
    },
  ),
)

export default useChatStore
